import React, { useState } from "react";
import Offcanvas from "react-bootstrap/Offcanvas";
import classNames from "classnames";
import { formOffCanvas, formLink } from "@/assets/styles/common.module.scss";
import { calculateAge, titleCaseFormat, toAmountFloat } from "@/helpers/utils";
import { link } from "@/components/quote-card/quote.module.scss";
import PropTypes from "prop-types";
import Image from "next/image";
import { IMAGE_BASE_PATH } from "@/config/config";
import { toAmount } from "@/helpers/utils";
import { PRODUCT_CODE, FLOATER_PLAN_TYPE } from "@/config/constants";

const OffcanvasMenu = React.memo(({ data }) => {
  const [show, setShow] = useState(false);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const divider = (
    <div
      className="mt-2 mb-3 d-flex align-self-stretch"
      style={{ border: "1px dashed #bcbcbc" }}
    />
  );

  const closeButton = (
    <div aria-hidden="true" className={"my-auto pointer"} onClick={handleClose}>
      <Image
        src={`${IMAGE_BASE_PATH}/images/close.svg`}
        alt="X"
        width={15}
        height={15}
      />
    </div>
  );

  const addDetail = (condition, label, value, id, details) => {
    if (condition) {
      const hasDecimal = (num) => num % 1 !== 0;
      const numericValue = parseFloat(value);
      const formattedValue = !isNaN(numericValue)
        ? `₹ ${
            hasDecimal(numericValue)
              ? toAmountFloat(numericValue.toFixed(2))
              : toAmount(numericValue)
          }`
        : value;

      details.push({ label, value: formattedValue, id });
    }
  };

  const mapProposalDetails = (data, isFloater) => {
    const proposalDetails = [];
    const isCritiMedicare = data?.productCode === PRODUCT_CODE.CRITI_MEDICARE;
    const isMedicarePlus = data?.productCode === PRODUCT_CODE.MEDICARE_PLUS;

    const planType = data?.planType;
    const globalCover = data?.globalCover === true ? "Yes" : "No";
    const memberDetails = data?.memberDetails[0];

    const applicableSI = memberDetails?.applicableSI;
    const cbAmount = memberDetails?.cbAmount;
    const deductibleAmt = memberDetails?.deductibleAmt;
    const ciSI = memberDetails?.ciSI;
    const cancerSI = memberDetails?.cancerSI;
    const hospitalCashSI = memberDetails?.hospitalCashSI;

    const criticalIllnessPlanType = data?.criticalIllnessPlanType;
    const hospitalCashSectionPeriod = data?.hospitalCashSectionPeriod;
    const wellsuranceBenefit = data?.wellsuranceBenefit;
    const ciSectionSurvivalPeriod = data?.ciSectionSurvivalPeriod;
    const superChargeBonus = data?.premiumSchemes?.[0]?.memberDetails?.[0]?.superChargeBonus;

    addDetail(planType, "Plan Type", planType, "planType", proposalDetails);

    if (isFloater) {
      addDetail(
        !isCritiMedicare && applicableSI,
        "Sum Insured",
        applicableSI,
        "sumInsured",
        proposalDetails
      );
      addDetail(
        cbAmount,
        "Cumulative Bonus",
        cbAmount,
        "cumulativeBonus",
        proposalDetails
      );
      addDetail(
        superChargeBonus,
        "Super Charge Bonus",
        superChargeBonus,
        "superChargeBonus",
        proposalDetails
      );
      addDetail(
        isMedicarePlus && deductibleAmt,
        "Deductible",
        deductibleAmt,
        "deductible",
        proposalDetails
      );
      addDetail(
        isCritiMedicare && ciSI,
        "Critical Illness SI",
        ciSI,
        "criticalIllness",
        proposalDetails
      );
      addDetail(
        isCritiMedicare && cancerSI,
        "Cancer 360 SI",
        cancerSI,
        "cancer360",
        proposalDetails
      );
      addDetail(
        isCritiMedicare && hospitalCashSI,
        "Hospital Cash SI",
        hospitalCashSI,
        "hospitalCashPerDay",
        proposalDetails
      );
    }

    addDetail(
      data?.globalCover,
      "Global Cover",
      globalCover,
      "globalCoverVal",
      proposalDetails
    );
    addDetail(
      isCritiMedicare && criticalIllnessPlanType,
      "Critical Illness Plan",
      criticalIllnessPlanType,
      "criticalIllnessPlanType",
      proposalDetails
    );
    addDetail(
      isCritiMedicare && hospitalCashSectionPeriod,
      "Hospital Cash (No. of days)",
      hospitalCashSectionPeriod,
      "hospitalCashSectionPeriod",
      proposalDetails
    );
    addDetail(
      isCritiMedicare && wellsuranceBenefit,
      "Wellassurance Benefit",
      wellsuranceBenefit,
      "wellsuranceBenefit",
      proposalDetails
    );
    addDetail(
      isCritiMedicare && ciSectionSurvivalPeriod,
      "Survival Period",
      ciSectionSurvivalPeriod,
      "ciSectionSurvivalPeriod",
      proposalDetails
    );

    return proposalDetails;
  };

  const mapMemberDetails = (data, isFloater) => {
    return data.memberDetails.map((member, index) => {
      const memberLevelSI = [];
      const isCritiMedicare = data?.productCode === PRODUCT_CODE.CRITI_MEDICARE;
      const isMedicarePlus = data?.productCode === PRODUCT_CODE.MEDICARE_PLUS;

      if (!isFloater) {
        addDetail(
          isCritiMedicare && member?.ciSI,
          "Critical Illness SI",
          member.ciSI,
          "criticalIllness",
          memberLevelSI
        );
        addDetail(
          isCritiMedicare && member?.cancerSI,
          "Cancer 360 SI",
          member.cancerSI,
          "cancer360",
          memberLevelSI
        );
        addDetail(
          isCritiMedicare && member?.hospitalCashSI,
          "Hospital Cash SI",
          member.hospitalCashSI,
          "hospitalCashPerDay",
          memberLevelSI
        );
        addDetail(
          !isCritiMedicare && member?.applicableSI,
          "Sum Insured",
          member.applicableSI,
          "sumInsured",
          memberLevelSI
        );
        addDetail(
          member?.cbAmount,
          "Cumulative Bonus",
          member.cbAmount,
          "cumulativeBonus",
          memberLevelSI
        );
        addDetail(
          isMedicarePlus && member?.deductibleAmt,
          "Deductible",
          member.deductibleAmt,
          "deductible",
          memberLevelSI
        );
      }

      return {
        id: index.toString(),
        insuredName: member.insuredName,
        insuredAge: calculateAge(member.insuredDOB, data.expiringPolicyToDate),
        memberLevelSI: isFloater ? null : memberLevelSI,
      };
    });
  };

  const trimmedPlanType = data.planType.trim();
  const floaterPolicyCase =
    FLOATER_PLAN_TYPE.includes(trimmedPlanType) ||
    (trimmedPlanType === "Individual Basis" && data.memberDetails.length === 1);

  const proposalDetails = mapProposalDetails(data, floaterPolicyCase);
  const memberDetails = mapMemberDetails(data, floaterPolicyCase);

  return (
    <>
      <span>
        {/* <button className={styles.linkButton} onClick={handleShow}>
          Show More
        </button> */}
        <a
          onClick={handleShow}
          // href="#"
          className={classNames(formLink, link)}
          id="renew-health-viewpremium"
        >
          View Additional Details
        </a>
      </span>
      <Offcanvas
        className={classNames(formOffCanvas, "rQuoteCard")}
        show={show}
        onHide={handleClose}
        placement="end"
      >
        <Offcanvas.Header style={{ display: "flex", justifyContent: "space-between" }}>
          <Offcanvas.Title>Policy Description</Offcanvas.Title>
          {closeButton}
        </Offcanvas.Header>
        <Offcanvas.Body>
          <div className="mb-2">Policy Details</div>

          {proposalDetails &&
            proposalDetails.map((policy) => {
              return (
                <div
                  key={policy.id}
                  className="rContentClass d-flex justify-content-between"
                >
                  <label>{policy.label}:</label>
                  <div>{policy.value}</div>
                </div>
              );
            })}

          {divider}
          <div>Member Details</div>
          {memberDetails &&
            memberDetails.map((row) => {
              const memberInfo = (
                <div className="py-1" key={row.id}>
                  {titleCaseFormat(row.insuredName)}{" "}
                  <span className="rContentClass">(Age {row.insuredAge})</span>
                </div>
              );
              const memberwiseSIElement =
                row.memberLevelSI &&
                row.memberLevelSI.map((member) => {
                  return (
                    <div
                      key={member.id} // Assign a unique key
                      className="rContentClass"
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(2, 1fr)",
                      }}
                    >
                      <div className="border border-1 border-dark ps-1">
                        {member.label}
                      </div>
                      <div className="border border-1 border-dark ps-1">
                        {member.value}
                      </div>
                    </div>
                  );
                });
              return (
                <React.Fragment key={row.id}>
                  {memberInfo}
                  <div className="ms-3 mb-3">{memberwiseSIElement}</div>
                </React.Fragment>
              );
            })}
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
});

OffcanvasMenu.propTypes = {
  data: PropTypes.shape({
    criticalIllnessPlanType: PropTypes.string,
    hospitalCashSectionPeriod: PropTypes.string,
    wellsuranceBenefit: PropTypes.string,
    ciSectionSurvivalPeriod: PropTypes.number,
    planType: PropTypes.string,
    memberDetails: PropTypes.array,
    applicableSI: PropTypes.string,
    cbAmount: PropTypes.string,
    deductibleAmt: PropTypes.string,
    globalCover: PropTypes.bool,
    productCode: PropTypes.number,
    expiringPolicyToDate: PropTypes.string,
    premiumSchemes: PropTypes.arrayOf(
      PropTypes.shape({
        memberDetails: PropTypes.arrayOf(
          PropTypes.shape({
            superChargeBonus: PropTypes.number,
          })
        ),
      })
    ),
  }),
};

OffcanvasMenu.displayName = "OffcanvasMenu";

export default OffcanvasMenu;
